import {
  Component,
  effect,
  EventEmitter,
  inject,
  model,
  ModelSignal,
  Renderer2,
  Signal,
  signal,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import {
  ColTypeColDefMap,
  CSV_EXPORT_ACTION,
  DialogService,
  DisplayedColumns,
  ICustomGridRow,
  IRowDataChange,
  ITableAction,
  ReasoningDialogComponent,
  SharedGridOptions,
  TableApiAdapter,
  TableControlComponent,
  TableControlModule,
  ToolbarStore,
} from 'frontier/browserkit';
import {
  changes,
  ControlService,
  EControlActions,
  EsvgFiles,
  IControlDataChanged,
  IControlObject,
  INITIAL_STATE_TOKEN,
} from 'frontier/nucleus';
import {GridOptions, RowNode} from 'ag-grid-community';
import {MatDialogRef} from '@angular/material/dialog';
import {StatusDialogComponent} from './status-dialog/status-dialog.component';
import {
  combineLatest,
  distinctUntilChanged,
  merge,
  Observable,
  of,
  scan,
  shareReplay,
  startWith,
  Subscription,
  switchMap,
  Unsubscribable,
} from 'rxjs';
import {catchError, concatMap, filter, finalize, map, tap} from 'rxjs/operators';
import {CartCreationService} from './cart-creation.service';
import {NewMissionDialogComponent} from './new-mission-dialog/new-mission-dialog.component';
import {INewMissionDialog} from './new-mission-dialog/new-mission-dialog.interface';
import {IPdfOverlayToggleEvent} from './pdf-overlay-toggle-event.interface';
import {areSetsEqual} from 'frontier/nucleus/src/lib/utils/equal-sets.func';
import {ECalculusStateFilter} from '../../../config/state-filter.enum';
import {ProcessOverviewStore} from '../service/process-overview.store';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {AppService, CartListService, OpenItemListService} from 'src/app/api/generated';
import AppControlGuids from '../../../../api/ControlGuids';
import {ICart, IClient, IInvoice, IPdfOverlay, IProcessState, TPdfOverlay} from '../service/state.interface';
import {TShownTables} from '../shown-tables.type';
import {ThemePalette} from '@angular/material/core';

export class CartsApiAdapter extends TableApiAdapter {
  checkedIndex: number;

  override from(apiData: any): any {
    const filteredColumns: DisplayedColumns[] = apiData.displayedColumns.filter((col: DisplayedColumns, index: number) => {
      if (col.attribute === 'Checked') {
        this.checkedIndex = index;
      }
      return col.attribute !== 'Checked' && col.attribute !== 'AttachmentNoteCombined';
    });
    return {
      displayedColumns: filteredColumns,
      columnDefs: this.createGridCols(filteredColumns),
      name: null,
      objecttype: apiData.objecttype,
      entityName: null,
      parentobj: apiData.parentobj,
    };
  }
}

@Component({
  selector: 'kpi4me-carts',
  templateUrl: '../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrls: [
    'carts.component.scss',
    '../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss'
  ],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: [TableControlModule],
  providers: [
    {provide: INITIAL_STATE_TOKEN, useValue: {filter: null}},
    {provide: TableControlComponent, useExisting: CartsComponent},
  ],
})
export class CartsComponent extends TableControlComponent {
  protected readonly EControlActions: typeof EControlActions = EControlActions;
  private _toolbarStore: ToolbarStore = inject(ToolbarStore);
  private _controlService: ControlService = inject(ControlService);
  private _appService: AppService = inject(AppService);
  private _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  private _renderer: Renderer2 = inject(Renderer2);
  private _dialogService: DialogService = inject(DialogService);
  private _openItemsService: OpenItemListService = inject(OpenItemListService);
  private _cartListService: CartListService = inject(CartListService);
  private _cartCreationService: CartCreationService = inject(CartCreationService);
  private rowSelection: EventEmitter<IControlObject> = new EventEmitter<IControlObject>();
  pdfOverlay: Signal<IPdfOverlay> = this._processOverviewService.pdfOverlay;
  selectedProcess: Signal<IProcessState> = this._processOverviewService.selectedProcess;
  selectedInvoice: Signal<IInvoice> = this._processOverviewService.selectedInvoice;
  selectedCart: Signal<ICart> = this._processOverviewService.selectedCart;
  cartHasCopayInvoice: WritableSignal<boolean> = signal<boolean>(false);
  selectedCart$: Observable<ICart> = toObservable(this._processOverviewService.selectedCart);
  selectedProcess$: Observable<IProcessState> = toObservable(this.selectedProcess);
  selectedInvoice$: Observable<IInvoice> = toObservable(this.selectedInvoice);
  selectedClient$: Observable<IClient> = toObservable(this._processOverviewService.selectedClient);
  tableTabs: Signal<Set<TShownTables>> = this._processOverviewService.tableTabs;
  tableTabs$: Observable<Set<TShownTables>> = toObservable(this.tableTabs);
  isPdfLoading: Signal<boolean> = this._processOverviewService.isPdfLoading;
  pdfTrapoToggle$: EventEmitter<IPdfOverlayToggleEvent> = new EventEmitter<IPdfOverlayToggleEvent>();
  pdfInvoiceToggle$: EventEmitter<IPdfOverlayToggleEvent> = new EventEmitter<IPdfOverlayToggleEvent>();
  pdfCollectiveInvoiceToggle$: EventEmitter<IPdfOverlayToggleEvent> = new EventEmitter<IPdfOverlayToggleEvent>();
  pdfDetailDocumentToggle$: EventEmitter<IPdfOverlayToggleEvent> = new EventEmitter<IPdfOverlayToggleEvent>();
  pdfDetailTrapoDocumentToggle$: EventEmitter<IPdfOverlayToggleEvent> = new EventEmitter<IPdfOverlayToggleEvent>();
  readonly dynamicText: WritableSignal<string> = signal<string>('');
  readonly footerSpan: WritableSignal<HTMLElement> = signal<HTMLElement>(null);

  dialogRef: MatDialogRef<StatusDialogComponent>;

  override GUID: string = AppControlGuids.TCartList;
  override apiAdapter: CartsApiAdapter = new CartsApiAdapter({}, ColTypeColDefMap);

  override gridOptions: GridOptions = {
    ...SharedGridOptions,
    pagination: true,
    rowSelection: 'single',
  };

  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    {
      displayName: 'Erzeugen',
      controlAction: EControlActions.invoice,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'primary',
      icon: EsvgFiles.document,
      tooltip: 'Erstellt alle erstellbaren, noch nicht erstellten Rechnungen gemäß den aktuell gesetzten Filtern und ' +
        'dem Breadcrumb.',
      tooltipDelay: 1000,
      action: (): Observable<boolean> => this.createAllInvoices(),
      isHidden: (): boolean => {
        return !(this.selectedProcess().category === ECalculusStateFilter.createInvoice)
          || this.selectedProcess()?.state === ECalculusStateFilter.createInvoiceOk;
      },
    },
    {
      displayName: 'ELSID ändern',
      controlAction: EControlActions.changeElsid,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'primary',
      icon: EsvgFiles.edit,
      tooltip: `Die ELSID kann über eine Eingabe geändert werden`,
      tooltipDelay: 1000,
      action: (): Observable<any> => this.changeElsid(),
      isHidden: (): boolean => {
        return !(
            [
              ECalculusStateFilter.kpiConnect,
              ECalculusStateFilter.ETLnMapping,
              ECalculusStateFilter.check,
              ECalculusStateFilter.createInvoice
            ].includes(this.selectedProcess()?.category)
          )
          || [
            ECalculusStateFilter.kpiConnectOk,
            ECalculusStateFilter.ETLnMappingOk,
            ECalculusStateFilter.checkOk,
            ECalculusStateFilter.createInvoiceOk
          ].includes(this.selectedProcess()?.state)
      },
    },
    {
      displayName: (): string => {
        return this.pdfOverlay()?.type === 'trapo' ? 'Trapo ausblenden' : 'Trapo anzeigen';
      },
      controlAction: EControlActions.preview,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'primary',
      icon: EsvgFiles.search,
      action: (): Observable<null> => of(null).pipe(tap((): void => this.pdfTrapoToggle$.emit({type: 'trapo'}))),
      isDisabled: (): boolean => {
        return this.isPdfLoading();
      },
      isHidden: (): boolean => {
        return !!this.selectedInvoice() || [
          ECalculusStateFilter.kpiConnect,
          ECalculusStateFilter.ETLnMapping
        ].includes(this.selectedProcess()?.category);
      },
    },
    {
      displayName: (): string => {
        return this.pdfOverlay()?.type === 'invoice' ? 'Rechnung ausblenden' : 'Rechnung anzeigen';
      },
      controlAction: EControlActions.invoicePreview,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'primary',
      icon: EsvgFiles.search,
      action: (): Observable<null> => of(null).pipe(tap((): void => this.pdfInvoiceToggle$
        .emit({type: 'invoice'}))),
      isDisabled: (): boolean => {
        return this.isPdfLoading();
      },
      isHidden: (): boolean => {
        return !!this.selectedInvoice() || [
          ECalculusStateFilter.kpiConnect,
          ECalculusStateFilter.ETLnMapping,
          ECalculusStateFilter.check,
          ECalculusStateFilter.createInvoice
        ].includes(this.selectedProcess()?.category);
      },
    },
    {
      displayName: (): string => {
        return this.pdfOverlay()?.type === 'copaymentInvoice' ?
          'Eigenanteilrechnung ausblenden' : 'Eigenanteilrechnung anzeigen';
      },
      controlAction: EControlActions.copaymentInvoicePreview,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'primary',
      icon: EsvgFiles.search,
      action: (): Observable<null> => of(null).pipe(tap((): void => this.pdfInvoiceToggle$
        .emit({type: 'copaymentInvoice'}))),
      isDisabled: (): boolean => {
        return this.isPdfLoading();
      },
      isHidden: (): boolean => {
        return [
            ECalculusStateFilter.kpiConnect,
            ECalculusStateFilter.ETLnMapping,
            ECalculusStateFilter.check,
            ECalculusStateFilter.createInvoice
          ].includes(this.selectedProcess()?.category) ||
          !this.cartHasCopayInvoice();
      },
    },
    {
      displayName: 'Neuer Einsatz',
      controlAction: EControlActions.newMission,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'primary',
      icon: EsvgFiles.add,
      action: (): Observable<any> => this._cartCreationService.createMission(
        this.instanceId(),
        {onFinalize: (): void => this.gridApi?.hideOverlay()},
      ),
      isHidden: (): boolean => {
        return !(this.selectedProcess()?.category === ECalculusStateFilter.ETLnMapping);
      },
    },
    {
      controlAction: EControlActions.check,
      displayName: (): string => {
        if (this.gridApi == null || this.selectedProcess()?.category == null) return 'Geprüft';

        const selectedRows: any[] = this.gridApi.getSelectedRows();
        const categoryId: ECalculusStateFilter = this.selectedProcess()?.category || null;

        return selectedRows.length !== 1 || selectedRows[0][this.apiAdapter.checkedIndex]?.value ||
        categoryId === ECalculusStateFilter.check || categoryId === ECalculusStateFilter.kpiConnect ?
          'Geprüft' : 'Prüfung anfordern';
      },
      color: (): ThemePalette => {
        if (this.gridApi == null) return 'accent';
        const selectedRows: any[] = this.gridApi.getSelectedRows();
        return selectedRows.length !== 1 ? 'primary' : 'accent';
      },
      icon: (): string => {
        if (this.gridApi == null) return '';
        const selectedRows: any[] = this.gridApi.getSelectedRows();
        return selectedRows.length !== 1 ? '' : selectedRows[0][this.apiAdapter.checkedIndex]?.value ?
          EsvgFiles.list_checked : '';
      },
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      action: (): Observable<any> => {
        if (this.gridApi == null || this.selectedProcess()?.category == null) return this.toggleChecked();
        const categoryId: ECalculusStateFilter = this.selectedProcess()?.category || null;

        const selectedRows: any[] = this.gridApi.getSelectedRows();
        return selectedRows.length !== 1 || selectedRows[0][this.apiAdapter.checkedIndex]?.value
        || categoryId === ECalculusStateFilter.check || categoryId === ECalculusStateFilter.kpiConnect ?
          this.toggleChecked() : this.manualCheck();
      },
      isHidden: (): boolean => {
        return ![
            ECalculusStateFilter.check,
            ECalculusStateFilter.createInvoice,
            ECalculusStateFilter.ETLnMapping,
          ].includes(this.selectedProcess()?.category)
          || this.selectedProcess()?.state === ECalculusStateFilter.createInvoiceOk;
      },
    },
    {
      displayName: 'Mahnungen erstellen',
      controlAction: EControlActions.dunning,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'accent',
      icon: EsvgFiles.breaking_news,
      action: (): Observable<boolean> => this.createDunning(),
      isHidden: (): boolean => {
        return !(this.selectedProcess()?.state === ECalculusStateFilter.monitorPaymentError);
      },
    },
    CSV_EXPORT_ACTION,
  ]);


  // The currently selected row
  paginationSelectedRow$: Observable<IControlObject> = merge(
    this.paginationChanged$.pipe(
      map((paginationChange): IControlObject => {
        // only if the page size is one, the first row should be selected
        if (this.gridOptions.paginationPageSize === 1) {
          const topRowIndex = paginationChange.api.paginationGetCurrentPage();
          paginationChange.api.setFocusedCell(topRowIndex, '0');
          const firstRow: RowNode = paginationChange.api.getDisplayedRowAtIndex(topRowIndex);
          if (!firstRow?.data?.apiRow?.id) {
            return null;
          }
          this._rowSelection.setSelection(...[firstRow.data.apiRow.id]);
          return firstRow?.data?.apiRow.obj;
        }
        return null;
      }),
      filter((row: IControlObject): boolean => row != null)),
    this.selectedCart$.pipe(map((cart: ICart): IControlObject => cart?.obj))
  ).pipe(
    distinctUntilChanged((previous: IControlObject, current: IControlObject): boolean =>
      previous?.signature === current?.signature
    ),
    startWith(null)
  )

  // The type of overlay that is displayed
  pdfOverlayType$: Observable<IPdfOverlayToggleEvent | null> =
    merge(
      this.pdfTrapoToggle$,
      this.pdfInvoiceToggle$,
      this.pdfCollectiveInvoiceToggle$,
      this.pdfDetailDocumentToggle$,
      this.pdfDetailTrapoDocumentToggle$,
      this.tableTabs$.pipe(
        distinctUntilChanged((previous: Set<TShownTables>, current: Set<TShownTables>): boolean => {
          return areSetsEqual(previous, current);
        }),
        map(() => null)
      )
    ).pipe(
      scan((oldOverlay: IPdfOverlayToggleEvent, newOverlay: IPdfOverlayToggleEvent | null): IPdfOverlayToggleEvent => {
        if (newOverlay?.type === oldOverlay?.type &&
          ((newOverlay?.type != 'detailTrapoDocument' && newOverlay?.type != 'detailDocument') ||
            newOverlay?.data?.obj?.signature === oldOverlay?.data?.obj?.signature)) {
          return null;
        }
        return newOverlay;
      }, null),
      shareReplay(1)
    );

  // The pdf that is downloaded and should be displayed in the pdf overlay
  pdfBlob$: Subscription = combineLatest([
      this.pdfOverlayType$,
      this.paginationSelectedRow$,
    ]
  ).pipe(
    scan((old: [IPdfOverlayToggleEvent, IControlObject], newValue: [IPdfOverlayToggleEvent, IControlObject]):
    [IPdfOverlayToggleEvent, IControlObject] => {
      if (old[1]?.signature != newValue[1]?.signature &&
        (newValue[0]?.type === 'detailTrapoDocument' || newValue[0]?.type === 'detailDocument')) {
        return [null, newValue[1]] as [IPdfOverlayToggleEvent, IControlObject];
      }
      return newValue;
    }),
    tap((): void => this._processOverviewService.isPdfLoading$.next(true)),
    switchMap(([newOverlay, controlObj]: [IPdfOverlayToggleEvent, IControlObject]): Observable<IPdfOverlay> => {
      const newOverlayType: TPdfOverlay = newOverlay?.type || null;
      if (
        newOverlayType === 'collectiveInvoice' ||
        newOverlayType === 'detailDocument' ||
        newOverlayType === 'detailTrapoDocument'
      ) {
        controlObj = newOverlay?.data?.obj;
      }
      if (controlObj == null) {
        return of(null);
      }
      let req$: Observable<IPdfOverlay> = of(null);
      // If no current overlay is shown and the selected pagination row has changed, select it.
      if (newOverlayType === 'collectiveInvoice') {
        req$ = this._openItemsService.openItemListDownloadCurrentInvoice({openitem: controlObj}).pipe(
          map((v: Blob): IPdfOverlay => ({
            type: newOverlayType,
            data: {blob: v, externalData: false},
          }))
        );
      } else if (newOverlayType === 'invoice') {
        req$ = this._cartListService.cartListDownloadCurrentInvoice({cart: controlObj}).pipe(
          map((v: Blob): IPdfOverlay => ({
            type: newOverlayType,
            data: {blob: v, externalData: false},
          }))
        );
      } else if (newOverlayType === 'copaymentInvoice') {
        req$ = this._cartListService.cartListDownloadCurrentCopayInvoice({cart: controlObj}).pipe(
          map((v: Blob): IPdfOverlay => ({
            type: newOverlayType,
            data: {blob: v, externalData: false},
          }))
        );
      } else if (newOverlayType === 'trapo') {
        req$ = this._cartListService.cartListDownloadTrapo({cart: controlObj}).pipe(
          map((v: Blob): IPdfOverlay => ({
            type: newOverlayType,
            data: {blob: v, externalData: false},
          }))
        );
      } else if (newOverlayType === 'detailDocument' || newOverlayType === 'detailTrapoDocument') {
        req$ = this._controlService.controlGetDownloadDocument(
          newOverlay?.data?.apiInstance?.instanceid,
          controlObj.rowid.toString(),
          controlObj.type,
          controlObj.signature,
        ).pipe(
          map((v: Blob): IPdfOverlay => ({
            type: newOverlayType,
            data: {blob: v, externalData: false},
          }))
        );
      }
      return req$.pipe(catchError((): Observable<null> => {
        this.feedbackService.setError('Die PDF konnte nicht heruntergeladen werden.');
        return of(null);
      }))
    }),
    tap((res: IPdfOverlay | null): void => {
      this._processOverviewService.pdfOverlay$.next(res);
      this.gridApi?.hideOverlay();
      this._processOverviewService.isPdfLoading$.next(false);
    }),
    catchError((e: any): Observable<null> => {
        console.error(e);
        this._processOverviewService.pdfOverlay$.next(null)
        this.gridApi?.hideOverlay();
        this._processOverviewService.isPdfLoading$.next(false);
        return of(null);
      }
    )
  ).pipe(
    takeUntilDestroyed()
  ).subscribe();

  constructor() {
    super();

    effect((): void => {
      const selectedCart: ICart = this.selectedCart();

      // Refresh the carts table if the carts table is shown.
      if (selectedCart) {
        if (selectedCart?.replaced) {
          if (this._processOverviewService.isOnlyCartsTableDisplayed()) {
            this.refreshInfiniteCache();
          }
        }

        this._appService.cartListCartHasCopayInvoice({cart: this.selectedCart().obj})
          .subscribe((res: boolean): void => {
            this.cartHasCopayInvoice.set(!!res);
          });

      } else {
        this.gridApi?.deselectAll();
        this.gridApi?.setFilterModel(null);
      }
    });

    effect((): void => {
      // on change of the selected process, clear the filter and deselect all rows
      const selectedProcess: IProcessState = this.selectedProcess();
      if (selectedProcess && this.gridApi) {
        this.savedSelectedRowsIds = [];
      }
    });

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [AppControlGuids.TCartList,
              new Set([
                EControlActions.arrange,
                EControlActions.invoice,
                EControlActions.check,
                EControlActions.dunning,
              ])],
            [AppControlGuids.TInvoiceList, new Set([
              ...changes,
              EControlActions.cancel,
              EControlActions.uncollectible,
              EControlActions.passToCollectionAgency,
              EControlActions.payed,
              EControlActions.credit,
              EControlActions.reroll,
              EControlActions.partlyCancel
            ])],
            [AppControlGuids.TCartItemsList, new Set([...changes, EControlActions.partlyCancel])],
            [AppControlGuids.TClientList, changes],
            [AppControlGuids.TCrewList, changes],
            [AppControlGuids.TCrudCartAdditionaldata, changes],
            [AppControlGuids.TOrdinanceList, changes],
            [AppControlGuids.TTransportList, changes],
            [AppControlGuids.TNotBillable, changes],
            [AppControlGuids.TOpenItemList, new Set([EControlActions.dunning])],
            [null, new Set([EControlActions.resolveClientMessage])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((): void => this.refreshInfiniteCache())).subscribe(),
    );

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [AppControlGuids.TAllClientList, new Set([...changes, EControlActions.attendance])],
            [AppControlGuids.TPermanentOrdinanceForClient, new Set([EControlActions.delete])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((): Unsubscribable => this.changeAndFetchInstance())).subscribe(),
    );

    this.subs.add(
      this.rowDataChanged.subscribe((res: IRowDataChange): void => {
        if (res?.selected[0]?.apiRow.obj) {
          this.rowSelection.emit(res?.selected[0]?.apiRow.obj);
        } else {
          this.rowSelection.emit(null);
        }
      })
    );

    this.subs.add(this.rowSelection.pipe(
      distinctUntilChanged((prev: IControlObject, curr: IControlObject): boolean =>
        JSON.stringify(prev) === JSON.stringify(curr)
      ),
      tap((rowObj: IControlObject): void => {
        if (rowObj) {
          this._cartListService.cartListGetStateText({cart: rowObj}).subscribe((res: string): void => {
            this.dynamicText.set(res);
          });

          this._cartListService.cartListInternalNotes({cart: rowObj}).subscribe((res: string): void => {
            this._processOverviewService.internalNote$.next(res);
          });
        } else {
          this.dynamicText.set('');
          this._processOverviewService.internalNote$.next('');
        }
      }),
    ).subscribe());

    effect((): void => {
      const pdfOverlay: IPdfOverlay = this.pdfOverlay();
      if (
        pdfOverlay?.type === 'trapo' ||
        pdfOverlay?.type === 'invoice' ||
        pdfOverlay?.type === 'collectiveInvoice' ||
        pdfOverlay?.type === 'detailDocument' ||
        pdfOverlay?.type === 'detailTrapoDocument' ||
        pdfOverlay?.type === 'copaymentInvoice'
      ) {
        this.pdf.set(pdfOverlay?.data);
      } else {
        this.pdf.set(null);
      }
    }, {allowSignalWrites: true});

    // Update footer
    effect((): void => {
      const dynamicText: string = this.dynamicText();
      const footerSpan: HTMLElement = this.footerSpan();
      if (footerSpan) {
        footerSpan.textContent = dynamicText;
      }
    });

    this.rowSelected.pipe(
      takeUntilDestroyed()
    ).subscribe((rows: RowNode[]): void => {
      const row: RowNode = rows[0]
      const selectedRow: ICustomGridRow = row?.data as ICustomGridRow;
      if (!selectedRow?.apiRow) {
        return;
      }
      this.savedSelectedRowsIds = selectedRow?.apiRow?.id ? [selectedRow.apiRow.id] : [];
      this.rowSelection.emit(selectedRow.apiRow.obj);

      this._processOverviewService.selectedCart$.next({
        obj: selectedRow.apiRow.obj,
        number: selectedRow[0]?.value,
      })

      this.gridApi?.hideOverlay();
      this.controlStore.controlDataChanged$.emit({changeType: EControlActions.detail, GUID: this.GUID});
    })

    this.savedRowSelected.pipe(
      takeUntilDestroyed()
    ).subscribe((rows: RowNode[]): void => {
      const row: RowNode = rows[0]
      if (!row?.data?.apiRow) {
        return;
      }
      this.rowSelection.emit(row.data.apiRow.obj);
    })
  }


  override ngOnInit(): void {
    super.ngOnInit();
    this.subs.add(
      combineLatest([
        this._toolbarStore.toolbarFilterDto$,
        this.selectedProcess$,
        this.selectedInvoice$,
        this.selectedClient$
      ]).pipe(
        tap(([globalFilter, selectedProcess, selectedInvoice, selectedClient]) => {
          const {state, category} = selectedProcess;
          // Update the apiInstance and trigger the fetching of the data with the new filter.
          this.patchFilter({
            ...globalFilter,
            valuelist: typeof (state) === 'number' ? [state] : category ? [category] : undefined,
            typename: selectedInvoice?.invoiceId || (selectedClient?.name ? 'client' : null),
            parent: selectedClient?.obj || null,
          })
        }),
      ).subscribe()
    );
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    // Get the footerElement by class name
    const footerElement = this.elementRef.nativeElement.querySelector('.ag-paging-panel');
    const firstChild = this.elementRef.nativeElement.querySelector('.ag-paging-row-summary-panel');

    // Create a new span footerElement
    this.footerSpan.set(this._renderer.createElement('span'));
    this._renderer.addClass(this.footerSpan(), 'trapo-note'); // Add a custom class to the span

    // Add the span to the selected footerElement
    this._renderer.insertBefore(footerElement, this.footerSpan(), firstChild);
  }

  private createAllInvoices(): Observable<boolean> {
    return this._cartListService.cartListCreateAllInvoices({InstanceId: this.instanceId()})
      .pipe(
        finalize((): void => {
          this.gridApi?.hideOverlay();
        })
      );
  }

  private toggleChecked(): Observable<any> {
    return this._cartListService.cartListSetChecked({
      cart: this.selectedRows()[0].apiRow.obj
    });
  }

  private manualCheck(): Observable<boolean | null> {
    return this._dialogService.dialog.open(ReasoningDialogComponent, {
      data: {
        title: 'Begründung'
      }
    }).afterClosed().pipe(
      concatMap((reason: string): Observable<boolean> => {
          if (reason) {
            return this._cartListService.cartListSetManualCheckNeeded({
              reason,
              cart: this.selectedRows()[0].apiRow.obj
            })
          }
          return of(null);
        }
      ),
      finalize(
        (): void => this.gridApi?.hideOverlay()
      )
    )
  }

  createDunning(): Observable<boolean> {
    return this._cartListService.cartListCreateAllDunning({
      InstanceId: this.instanceId()
    });
  }

  private changeElsid(): Observable<any> {
    const cartRow: ICustomGridRow = this.selectedRows()[0];
    const missionNumber: any = cartRow[0].value;
    return this._dialogService.dialog.open(NewMissionDialogComponent, {
      width: '32rem',
      data: {
        instanceId: this.instanceId(),
        missionNumber: missionNumber,
        edit: true,
      } as INewMissionDialog,
    }).afterClosed().pipe(
      concatMap(result => {
        if (!result) {
          return of(null);
        }
        return this._cartListService.cartListChangeCartNumber({
          cart: cartRow.apiRow.obj,
          number: result.missionNumber
        }).pipe(
          tap((): void => {
            this.refreshInfiniteCache()
          })
        )
      }),
    )
  }
}
